import { Product } from "@/api/product-api";
import { cn } from "@/libs/utils";
import Image from "next/image";
import React from "react";
import { GePrice } from "@/v2-components/GePrice";
import { Button } from "@/v2-components/ui/button";
import { AspectRatio } from "@/v2-components/ui/aspect-ratio";
import ChevronRightIcon from "@/v2-components/core/icon/chevron-right-icon";
import { useRouter } from "next/router";
import { useTrans } from "@/utils/useTrans";
import { useCartWithProducts } from "@/store/useCartWithProducts";
import ProductTagBadge from "@/components/tactsuit/components/ProductTagBadge";
import NotifyOrAddButton from "@/components/tactsuit/NotifyOrAddButton";
import { SizeLineCardCta } from "@/components/tactsuit/components/size-line-card-cta";
import { useProductPrice } from "@/hooks/useProductPrice";
import { Badge } from "@/v2-components/ui/badge";
import MFMHorizontalOutline from "@/v2-components/core/icon/mfm-horizontal-outline";
import { ProductUtil } from "@/utils/ProductUtil";
import Link from "next/link";
import { useLocale } from "@/store/locale-store";
import { getProductDetailHref } from "@/libs/product/detail-href";

type Props = {
  product: Product;
  className?: string;
  isLearnMore?: boolean;
  showDesc?: boolean;
  hasMadeForMeta?: boolean;
};

const ProductCard = ({
  product,
  isLearnMore,
  className,
  showDesc = true,
  hasMadeForMeta,
}: Props) => {
  const { t } = useTrans(["tactsuit"]);
  const router = useRouter();
  const { getProductPrice, getCartProductTagsBySlug } = useCartWithProducts();
  const { countryCode } = useLocale();
  const price = getProductPrice(product);
  const tags = getCartProductTagsBySlug(product.slug);
  const {
    displayOriginalPrice,
    displayDiscountAmount,
    displaySalePrice,
    displayDicountPercent,
  } = useProductPrice(price);

  const percentOff = t("{{percent}}% off", {
    ns: "gnb",
    percent: displayDicountPercent,
    defaultValue: `${displayDicountPercent}% off`,
  });

  // 이미지 클릭은 **항상 상세로** 보낸다(#5276).
  //
  // 종전엔 `product.hasPage` 로 갈렸고 두 갈래 모두 신고 대상이었다 — `true` 면 라우트가 없는
  // 액세서리가 404, `false` 면 이미지 클릭이 **장바구니 담기**라 상세로 갈 방법이 없었다.
  // 담기는 아래 `NotifyOrAddButton` 이 맡는다(자체 옵션/번들 시트 포함).
  //
  // 품절도 링크는 살린다 — 살 수 없는 것과 볼 수 없는 것은 다르다. 종전엔 품절이면
  // 커서까지 사라져 클릭 대상이 아닌 것처럼 보였다.
  const detailHref = getProductDetailHref(product.slug);

  return (
    <div
      className={cn(
        "group flex w-full max-w-[500px] flex-col items-center",
        className,
      )}
    >
      <div className="flex h-5 items-center justify-center gap-2">
        {tags?.map((tag) => (
          <ProductTagBadge key={tag} tag={tag} />
        ))}
      </div>

      <div className={cn("relative mt-3 w-full max-w-[224px]")}>
        <Link href={detailHref}>
          <AspectRatio>
            <Image
              src={product.thumbnail}
              alt={"product image"}
              fill
              className="laptop:hover-scale cursor-pointer"
            />
          </AspectRatio>
        </Link>
      </div>

      <div className="flex flex-col items-center gap-3 pt-6">
        <h4 className={cn("text-center text-base font-500 text-coolgray-90")}>
          {product.name}
        </h4>
        <MadeForMeta slug={product.slug} hasMadeForMeta={hasMadeForMeta} />
        {showDesc && (
          <p
            className={cn(
              "mx-auto line-clamp-2 h-8 max-w-[300px] px-4 text-center text-sm font-400 text-coolgray-90",
            )}
          >
            {product.shortDesc}
          </p>
        )}

        {price && (
          <div
            className={cn(
              "flex flex-col items-center justify-center gap-2 text-base laptop:flex-row",
              className,
            )}
          >
            {!!displayDicountPercent && (
              <Badge variant={"sale"} size={"sm"}>
                {percentOff}
              </Badge>
            )}
            <div
              className={cn("flex items-center justify-center gap-2 text-base")}
            >
              <p>
                <GePrice
                  amount={displaySalePrice}
                  currency={price.currency}
                  countryCode={countryCode}
                />
              </p>
              {!!displayDiscountAmount && (
                <p className={cn("text-coolgray-50 line-through")}>
                  <GePrice
                    amount={displayOriginalPrice}
                    currency={price.currency}
                    countryCode={countryCode}
                  />
                </p>
              )}
            </div>
          </div>
        )}
      </div>
      {isLearnMore ? (
        <Button
          variant="blue-text"
          size="sm"
          className="mt-7 gap-2 pl-6 pr-4"
          onClick={() => {
            router.push(detailHref);
          }}
        >
          <span>{t("Learn More")}</span>
          <ChevronRightIcon className="size-4.5" />
        </Button>
      ) : (
        // 접힌 사이즈 라인 카드는 담지 않고 PDP 로 — 대표 사이즈가 그대로 담겼다(#5922 R1).
        <SizeLineCardCta product={product} className="mt-7">
          <NotifyOrAddButton
            trigger="product_card"
            className="mt-7"
            size="sm"
            productSlug={product.slug}
          />
        </SizeLineCardCta>
      )}
    </div>
  );
};

const MadeForMeta = ({ hasMadeForMeta, slug }) => {
  if (!hasMadeForMeta) return null;

  return (
    <MFMHorizontalOutline
      className={cn(
        "h-[28px] w-[127px]",
        ProductUtil.isMadeForMeta(slug) ? "" : "invisible",
      )}
    />
  );
};

export default ProductCard;
